import React, { useState, useEffect } from 'react';
import { 
  Play, 
  Video, 
  Clock, 
  FileText, 
  ChevronDown, 
  ChevronRight,
  ExternalLink,
  Download,
  Eye,
  Mic,
  Loader2,
  CheckCircle,
  AlertCircle,
  Volume2,
  Upload,
  X,
  Edit,
  Save
} from 'lucide-react';

const VideosPage = () => {
  const [productionScripts, setProductionScripts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedScripts, setExpandedScripts] = useState(new Set());
  const [groupedScripts, setGroupedScripts] = useState({});
  const [voices, setVoices] = useState([]);
  const [avatars, setAvatars] = useState([]);
  const [generatingAudio, setGeneratingAudio] = useState(new Set());
  const [generatingVideo, setGeneratingVideo] = useState(new Set());
  const [downloadingVideo, setDownloadingVideo] = useState(new Set());
  const [selectedVoices, setSelectedVoices] = useState({});
  const [selectedAvatars, setSelectedAvatars] = useState({});
  const [selectedFormats, setSelectedFormats] = useState({}); // New state for video formats
  const [uploadingVideo, setUploadingVideo] = useState(new Set());
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [avatarModalScriptId, setAvatarModalScriptId] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(null); // scriptId when modal is open
  const [completedVideos, setCompletedVideos] = useState({}); // scriptId -> array of completed videos
  const [editingScript, setEditingScript] = useState(null); // scriptId being edited
  const [editedContent, setEditedContent] = useState(''); // edited script content
  const [savingScript, setSavingScript] = useState(false); // saving state

  // Fetch production-ready scripts
  const fetchProductionScripts = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/videos/production-scripts`);
      const data = await response.json();
      
      if (data.success) {
        setProductionScripts(data.scripts);
        groupScriptsByDocument(data.scripts);
        
        // Set default formats to 9:16 for all scripts
        const defaultFormats = {};
        data.scripts.forEach(item => {
          defaultFormats[item.script.id] = '9:16';
        });
        setSelectedFormats(defaultFormats);
        
        // Fetch completed videos for all scripts on initial load
        data.scripts.forEach(item => {
          fetchCompletedVideos(item.script.id);
        });
      } else {
        console.error('Failed to fetch production scripts');
      }
    } catch (error) {
      console.error('Error fetching production scripts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch available voices
  const fetchVoices = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/voices`);
      const data = await response.json();
      setVoices(data.voices || []);
      
      // Set default voice to 'YPN Ant Lyons' for all scripts
      const ypnVoice = (data.voices || []).find(voice => voice.name === 'YPN Ant Lyons');
      if (ypnVoice) {
        setSelectedVoices(prev => {
          const newVoices = { ...prev };
          // Set default for all scripts that don't already have a voice selected
          productionScripts.forEach(item => {
            if (!prev[item.script.id]) {
              newVoices[item.script.id] = ypnVoice.voice_id;
            }
          });
          return newVoices;
        });
      }
    } catch (error) {
      console.error('Error fetching voices:', error);
    }
  };

  // Fetch available avatars
  const fetchAvatars = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/avatars/available`);
      const data = await response.json();
      setAvatars(data.avatars || []);
    } catch (error) {
      console.error('Error fetching avatars:', error);
    }
  };

  // Generate audio for a script
  const generateAudio = async (scriptId, voiceId = null) => {
    setGeneratingAudio(prev => new Set([...prev, scriptId]));
    
    try {
      const formData = new FormData();
      formData.append('script_id', scriptId);
      if (voiceId) {
        formData.append('voice_id', voiceId);
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/videos/generate-audio`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();
      
      if (data.success) {
        // Refresh the scripts to show updated audio status
        await fetchProductionScripts();
        console.log('Audio generated successfully:', data.message);
      } else {
        console.error('Failed to generate audio:', data.detail);
      }
    } catch (error) {
      console.error('Error generating audio:', error);
    } finally {
      setGeneratingAudio(prev => {
        const newSet = new Set(prev);
        newSet.delete(scriptId);
        return newSet;
      });
    }
  };

  // Generate video for a script
  const generateVideo = async (scriptId, avatarId = null, format = "9:16") => {
    setGeneratingVideo(prev => new Set([...prev, scriptId]));
    
    try {
      const formData = new FormData();
      formData.append('script_id', scriptId);
      if (avatarId) {
        formData.append('avatar_id', avatarId);
      }
      formData.append('format', format);

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/videos/generate-video`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();
      
      if (data.success) {
        // Refresh the scripts to show updated video status
        await fetchProductionScripts();
        console.log('Video generation started:', data.message);
      } else {
        console.error('Failed to generate video:', data.detail);
      }
    } catch (error) {
      console.error('Error generating video:', error);
    } finally {
      setGeneratingVideo(prev => {
        const newSet = new Set(prev);
        newSet.delete(scriptId);
        return newSet;
      });
    }
  };

  // Group scripts by document for better organization
  const groupScriptsByDocument = (scripts) => {
    const grouped = {};
    scripts.forEach(item => {
      const docId = item.document.id;
      if (!grouped[docId]) {
        grouped[docId] = {
          document: item.document,
          scripts: []
        };
      }
      grouped[docId].scripts.push(item.script);
    });
    setGroupedScripts(grouped);
  };

  // Toggle script expansion
  const toggleScript = (scriptKey) => {
    setExpandedScripts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(scriptKey)) {
        newSet.delete(scriptKey);
      } else {
        newSet.add(scriptKey);
      }
      return newSet;
    });
  };

  // Generate proper Supabase public URL for PDF
  const getSupabasePdfUrl = (storagePath) => {
    // Remove "pdfs/" prefix if it exists (legacy format)
    let cleanPath = storagePath;
    if (cleanPath.startsWith('pdfs/')) {
      cleanPath = cleanPath.substring(5);
    }
    
    // If it's already a full URL (old format), return as-is
    if (cleanPath.startsWith('https://')) {
      return cleanPath;
    }
    
    // Construct proper Supabase public URL
    const supabaseUrl = 'https://gvzakaegbycuawdvigap.supabase.co';
    return `${supabaseUrl}/storage/v1/object/public/pdfs/${cleanPath}`;
  };

  // Handle PDF preview
  const handlePreviewPdf = (storagePath) => {
    const pdfUrl = getSupabasePdfUrl(storagePath);
    const googleDocsUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(pdfUrl)}&embedded=true`;
    window.open(googleDocsUrl, '_blank');
  };

  // Handle PDF download
  const handleDownloadPdf = async (storagePath, title) => {
    try {
      const pdfUrl = getSupabasePdfUrl(storagePath);
      const response = await fetch(pdfUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF. Please try again or contact support.');
    }
  };

  // Handle video download
  const handleDownloadVideo = async (videoUrl, scriptId, scriptIndex) => {
    setDownloadingVideo(prev => new Set([...prev, scriptId]));
    
    try {
      console.log('Downloading video...');
      
      const response = await fetch(videoUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `script_${scriptIndex}_video.mp4`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      console.log('Video download completed');
    } catch (error) {
      console.error('Error downloading video:', error);
      // Fallback to opening in new tab if download fails
      window.open(videoUrl, '_blank');
    } finally {
      setDownloadingVideo(prev => {
        const newSet = new Set(prev);
        newSet.delete(scriptId);
        return newSet;
      });
    }
  };

  // Extract narrator text from script JSON
  const getNarratorText = (script) => {
    try {
      if (script.script_json && script.script_json.narrator_text) {
        return script.script_json.narrator_text;
      } else if (script.script_text) {
        return script.script_text;
      } else {
        return 'No script content available';
      }
    } catch (error) {
      return 'Error parsing script content';
    }
  };

  // Fetch completed videos for a script
  const fetchCompletedVideos = async (scriptId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v6/scripts/${scriptId}/completed-videos`);
      const data = await response.json();
      
      if (data.success) {
        setCompletedVideos(prev => ({
          ...prev,
          [scriptId]: data.videos
        }));
      }
    } catch (error) {
      console.error('Error fetching completed videos:', error);
    }
  };

  // Handle video upload (now with Google Drive URL)
  const handleVideoUpload = async (scriptId, file, metadata = {}) => {
    setUploadingVideo(prev => new Set([...prev, scriptId]));
    
    try {
      const formData = new FormData();
      
      // Add Google Drive URL (file parameter is null, URL is in metadata)
      if (metadata.google_drive_url) {
        formData.append('google_drive_url', metadata.google_drive_url);
      }
      
      // Add metadata
      if (metadata.title) formData.append('title', metadata.title);
      if (metadata.description) formData.append('description', metadata.description);
      if (metadata.format) formData.append('format', metadata.format);
      if (metadata.resolution) formData.append('resolution', metadata.resolution);
      if (metadata.upload_notes) formData.append('upload_notes', metadata.upload_notes);
      if (metadata.uploaded_by) formData.append('uploaded_by', metadata.uploaded_by);
      if (metadata.platform_optimized) formData.append('platform_optimized', JSON.stringify(metadata.platform_optimized));
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v6/scripts/${scriptId}/upload-completed-video`, {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert('Video uploaded successfully!');
        setShowUploadModal(null);
        // Refresh completed videos for this script
        await fetchCompletedVideos(scriptId);
      } else {
        throw new Error(data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Error uploading video:', error);
      alert(`Failed to upload video: ${error.message}`);
    } finally {
      setUploadingVideo(prev => {
        const newSet = new Set(prev);
        newSet.delete(scriptId);
        return newSet;
      });
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      await fetchProductionScripts();
      await fetchVoices();
      await fetchAvatars();
    };
    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load completed videos when scripts are expanded
  useEffect(() => {
    expandedScripts.forEach(scriptKey => {
      const scriptId = parseInt(scriptKey.split('-')[1]);
      if (scriptId && !completedVideos[scriptId]) {
        fetchCompletedVideos(scriptId);
      }
    });
  }, [expandedScripts, completedVideos]);

  // Handle script editing
  const handleEditScript = (scriptId, currentContent) => {
    setEditingScript(scriptId);
    setEditedContent(currentContent);
  };

  const handleCancelEdit = () => {
    setEditingScript(null);
    setEditedContent('');
  };

  const handleSaveScript = async (scriptId) => {
    setSavingScript(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v6/scripts/${scriptId}/update-content`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          script_text: editedContent
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        // Update the local state
        setProductionScripts(prev => prev.map(item => 
          item.script.id === scriptId 
            ? { ...item, script: { ...item.script, script_text: editedContent } }
            : item
        ));
        
        setEditingScript(null);
        setEditedContent('');
        
        // Show success message (you could add a toast notification here)
        console.log('Script updated successfully');
      } else {
        console.error('Failed to update script:', data.message);
        alert('Failed to update script. Please try again.');
      }
    } catch (error) {
      console.error('Error updating script:', error);
      alert('Error updating script. Please try again.');
    } finally {
      setSavingScript(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                <Video className="mr-3 h-8 w-8 text-purple-600" />
                Video Production
              </h1>
              <p className="text-sm text-gray-600">Scripts ready for audio and video processing</p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={fetchProductionScripts}
                disabled={loading}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                {loading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">Production Ready Scripts</h2>
            <div className="text-sm text-gray-500">
              {productionScripts.length} scripts ready for production
            </div>
          </div>

          {/* Scripts List */}
          <div className="bg-white rounded-lg shadow">
            {loading ? (
              <div className="p-8 text-center text-gray-500">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
                Loading production scripts...
              </div>
            ) : Object.keys(groupedScripts).length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Video className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Scripts Ready</h3>
                <p className="text-gray-600">No scripts have been submitted for production yet.</p>
                <p className="text-sm text-gray-500 mt-2">Scripts need to be submitted from the Library page first.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {Object.entries(groupedScripts).map(([docId, docData]) => (
                  <div key={docId} className="p-6">
                    {/* Document Header */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <FileText className="h-5 w-5 text-gray-400 mr-3" />
                        <div>
                          <h3 className="text-lg font-medium text-gray-900">{docData.document.title}</h3>
                          <p className="text-sm text-gray-500">
                            {docData.document.source_meta?.author || 'Unknown Author'} • 
                            Issue {docData.document.issue} • 
                            {docData.scripts.length} script{docData.scripts.length !== 1 ? 's' : ''} ready
                          </p>
                        </div>
                      </div>
                      
                      {/* Document Actions */}
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handlePreviewPdf(docData.document.storage_path)}
                          className="px-3 py-1 bg-purple-600 text-white text-xs rounded-md hover:bg-purple-700 flex items-center"
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          Preview
                        </button>
                        <a
                          href={getSupabasePdfUrl(docData.document.storage_path)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-1 bg-gray-600 text-white text-xs rounded-md hover:bg-gray-700 flex items-center"
                        >
                          <ExternalLink className="w-3 h-3 mr-1" />
                          Open
                        </a>
                        <button
                          onClick={() => handleDownloadPdf(docData.document.storage_path, docData.document.title)}
                          className="px-3 py-1 bg-gray-600 text-white text-xs rounded-md hover:bg-gray-700 flex items-center"
                        >
                          <Download className="w-3 h-3 mr-1" />
                          Download
                        </button>
                      </div>
                    </div>

                    {/* Scripts List */}
                    <div className="space-y-3">
                      {docData.scripts.map((script) => {
                        const scriptKey = `${docId}-${script.script_index}`;
                        const isExpanded = expandedScripts.has(scriptKey);
                        const narratorText = getNarratorText(script);
                        
                        return (
                          <div key={scriptKey} className="border border-gray-200 rounded-lg bg-gray-50">
                            <div 
                              className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-100"
                              onClick={() => toggleScript(scriptKey)}
                            >
                              <div className="flex items-center">
                                <div className="mr-3">
                                  {isExpanded ? (
                                    <ChevronDown className="w-4 h-4 text-gray-500" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4 text-gray-500" />
                                  )}
                                </div>
                                <div>
                                  <h4 className="text-sm font-medium text-gray-900">
                                    Script {script.script_index}: {script.script_json?.type || 'General'}
                                  </h4>
                                  {script.selected_headline && (
                                    <div className="mt-1 mb-2">
                                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        📰 {script.selected_headline}
                                      </span>
                                    </div>
                                  )}
                                  <p className="text-xs text-gray-500">
                                    Created: {new Date(script.created_at).toLocaleDateString()} • 
                                    Source: {script.source || 'AI'} • 
                                    Ready for production
                                  </p>
                                </div>
                              </div>
                              
                              <div className="flex items-center space-x-2">
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <Play className="w-3 h-3 mr-1" />
                                  Production Ready
                                </span>
                                {script.audio_status === 'ready' && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    <Volume2 className="w-3 h-3 mr-1" />
                                    Audio Ready
                                  </span>
                                )}
                                {script.video_status === 'ready' && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                    <Video className="w-3 h-3 mr-1" />
                                    Video Ready
                                  </span>
                                )}
                                {completedVideos[script.id] && completedVideos[script.id].length > 0 && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Complete
                                  </span>
                                )}
                                <Clock className="w-4 h-4 text-gray-400" />
                              </div>
                            </div>
                            
                            {isExpanded && (
                              <div className="px-4 pb-4 border-t border-gray-200">
                                {/* Selected Headline for Video Editor */}
                                {script.selected_headline && (
                                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                    <h5 className="text-xs font-medium text-blue-700 mb-1">Selected Headline for Video:</h5>
                                    <p className="text-sm font-medium text-blue-900">{script.selected_headline}</p>
                                  </div>
                                )}
                                
                                <div className="mt-3">
                                  <div className="flex items-center justify-between mb-2">
                                    <h5 className="text-xs font-medium text-gray-700">Script Content:</h5>
                                    {editingScript === script.id ? (
                                      <div className="flex items-center space-x-2">
                                        <button
                                          type="button"
                                          onClick={() => handleSaveScript(script.id)}
                                          disabled={savingScript}
                                          className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:opacity-50 flex items-center"
                                        >
                                          {savingScript ? (
                                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                          ) : (
                                            <Save className="w-3 h-3 mr-1" />
                                          )}
                                          Save
                                        </button>
                                        <button
                                          type="button"
                                          onClick={handleCancelEdit}
                                          className="px-2 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600 flex items-center"
                                        >
                                          <X className="w-3 h-3 mr-1" />
                                          Cancel
                                        </button>
                                      </div>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={() => handleEditScript(script.id, narratorText)}
                                        className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 flex items-center"
                                      >
                                        <Edit className="w-3 h-3 mr-1" />
                                        Edit
                                      </button>
                                    )}
                                  </div>
                                  
                                  {editingScript === script.id ? (
                                    <textarea
                                      value={editedContent}
                                      onChange={(e) => setEditedContent(e.target.value)}
                                      className="w-full h-64 p-3 border rounded text-sm text-gray-800 font-mono leading-relaxed resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                      placeholder="Edit your script content here..."
                                    />
                                  ) : (
                                    <div className="bg-white p-3 rounded border text-sm text-gray-800 max-h-64 overflow-y-auto">
                                      <pre className="whitespace-pre-wrap font-sans leading-relaxed">
                                        {narratorText}
                                      </pre>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Audio and Video Generation Controls */}
                                <div className="mt-4 space-y-4">
                                  {/* Audio Generation Section */}
                                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                                    <div className="flex items-center justify-between mb-3">
                                      <h6 className="text-sm font-medium text-blue-900 flex items-center">
                                        <Volume2 className="w-4 h-4 mr-2" />
                                        Audio Generation
                                      </h6>
                                      {script.audio_status && (
                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                          script.audio_status === 'ready' 
                                            ? 'bg-green-100 text-green-800' 
                                            : script.audio_status === 'generating'
                                            ? 'bg-yellow-100 text-yellow-800'
                                            : 'bg-red-100 text-red-800'
                                        }`}>
                                          {script.audio_status === 'ready' && <CheckCircle className="w-3 h-3 mr-1" />}
                                          {script.audio_status === 'generating' && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                                          {script.audio_status === 'failed' && <AlertCircle className="w-3 h-3 mr-1" />}
                                          {script.audio_status}
                                        </span>
                                      )}
                                    </div>
                                    
                                    <div className="flex items-center space-x-3">
                                      {voices.length > 0 && (
                                        <select
                                          value={selectedVoices[script.id] || ''}
                                          onChange={(e) => setSelectedVoices(prev => ({
                                            ...prev,
                                            [script.id]: e.target.value
                                          }))}
                                          className="text-xs border border-gray-300 rounded px-2 py-1"
                                        >
                                          <option value="">Default Voice</option>
                                          {voices.map(voice => (
                                            <option key={`script-${script.id}-voice-${voice.voice_id}`} value={voice.voice_id}>
                                              {voice.name}
                                            </option>
                                          ))}
                                        </select>
                                      )}
                                      
                                      <button
                                        onClick={() => generateAudio(script.id, selectedVoices[script.id])}
                                        disabled={generatingAudio.has(script.id)}
                                        className="px-3 py-1 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
                                      >
                                        {generatingAudio.has(script.id) ? (
                                          <>
                                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                            Generating...
                                          </>
                                        ) : (
                                          <>
                                            <Mic className="w-3 h-3 mr-1" />
                                            Generate Audio
                                          </>
                                        )}
                                      </button>
                                      
                                      {script.audio_url && (
                                        <>
                                          <audio controls className="h-8">
                                            <source src={script.audio_url} type="audio/mpeg" />
                                            Your browser does not support audio playback.
                                          </audio>
                                          <a
                                            href={script.audio_url}
                                            download={`script_${script.script_index}_audio.mp3`}
                                            className="px-3 py-1 bg-green-600 text-white text-xs rounded-md hover:bg-green-700 flex items-center"
                                          >
                                            <Download className="w-3 h-3 mr-1" />
                                            Download Audio
                                          </a>
                                        </>
                                      )}
                                    </div>
                                  </div>

                                  {/* Video Generation Section */}
                                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                                    <div className="flex items-center justify-between mb-3">
                                      <h6 className="text-sm font-medium text-purple-900 flex items-center">
                                        <Video className="w-4 h-4 mr-2" />
                                        Video Generation
                                      </h6>
                                      {script.video_status && (
                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                          script.video_status === 'ready' 
                                            ? 'bg-green-100 text-green-800' 
                                            : script.video_status === 'generating'
                                            ? 'bg-yellow-100 text-yellow-800'
                                            : script.video_status === 'failed'
                                            ? 'bg-red-100 text-red-800'
                                            : 'bg-gray-100 text-gray-800'
                                        }`}>
                                          {script.video_status === 'ready' && <CheckCircle className="w-3 h-3 mr-1" />}
                                          {script.video_status === 'generating' && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                                          {(script.video_status === 'failed' || script.video_status === 'timeout') && <AlertCircle className="w-3 h-3 mr-1" />}
                                          {script.video_status}
                                        </span>
                                      )}
                                    </div>
                                    
                                    <div className="flex items-center space-x-3">
                                      {avatars.length > 0 && (
                                        <button
                                          onClick={() => {
                                            setAvatarModalScriptId(script.id);
                                            setShowAvatarModal(true);
                                          }}
                                          className="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded-md hover:bg-gray-200 border border-gray-300 flex items-center"
                                        >
                                          <FileText className="w-3 h-3 mr-1" />
                                          {selectedAvatars[script.id] ? 'Change Avatar' : 'Select Avatar'}
                                        </button>
                                      )}
                                      
                                      <select
                                        value={selectedFormats[script.id] || '9:16'}
                                        onChange={(e) => setSelectedFormats(prev => ({
                                          ...prev,
                                          [script.id]: e.target.value
                                        }))}
                                        className="text-xs border border-gray-300 rounded px-2 py-1"
                                      >
                                        <option value="9:16">Portrait (9:16)</option>
                                        <option value="16:9">Landscape (16:9)</option>
                                      </select>
                                      
                                      <button
                                        onClick={() => generateVideo(script.id, selectedAvatars[script.id], selectedFormats[script.id] || '9:16')}
                                        disabled={generatingVideo.has(script.id) || !script.audio_url}
                                        className="px-3 py-1 bg-purple-600 text-white text-xs rounded-md hover:bg-purple-700 disabled:opacity-50 flex items-center"
                                      >
                                        {generatingVideo.has(script.id) ? (
                                          <>
                                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                            Generating...
                                          </>
                                        ) : (
                                          <>
                                            <Video className="w-3 h-3 mr-1" />
                                            Generate Video
                                          </>
                                        )}
                                      </button>
                                      
                                      {script.video_url && (
                                        <>
                                          <a
                                            href={script.video_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="px-3 py-1 bg-green-600 text-white text-xs rounded-md hover:bg-green-700 flex items-center"
                                          >
                                            <Play className="w-3 h-3 mr-1" />
                                            View Video
                                          </a>
                                          <button
                                            onClick={() => handleDownloadVideo(script.video_url, script.id, script.script_index)}
                                            disabled={downloadingVideo.has(script.id)}
                                            className="px-3 py-1 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
                                          >
                                            {downloadingVideo.has(script.id) ? (
                                              <>
                                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                                Downloading...
                                              </>
                                            ) : (
                                              <>
                                                <Download className="w-3 h-3 mr-1" />
                                                Download Video
                                              </>
                                            )}
                                          </button>
                                          <button
                                            onClick={() => setShowUploadModal(script.id)}
                                            className="px-3 py-1 bg-orange-600 text-white text-xs rounded-md hover:bg-orange-700 flex items-center"
                                          >
                                            <Upload className="w-3 h-3 mr-1" />
                                            Upload Completed
                                          </button>
                                        </>
                                      )}
                                    </div>
                                    
                                    {!script.audio_url && (
                                      <p className="text-xs text-purple-600 mt-2">
                                        Generate audio first to enable video creation
                                      </p>
                                    )}
                                  </div>

                                  {/* Completed Videos Section */}
                                  {completedVideos[script.id] && completedVideos[script.id].length > 0 && (
                                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                                      <h6 className="text-sm font-medium text-green-900 flex items-center mb-3">
                                        <CheckCircle className="w-4 h-4 mr-2" />
                                        Completed Videos ({completedVideos[script.id].length})
                                      </h6>
                                      <div className="space-y-2">
                                        {completedVideos[script.id].map((video, index) => (
                                          <div key={video.id} className="flex items-center justify-between bg-white p-2 rounded border">
                                            <div className="flex-1">
                                              <p className="text-sm font-medium text-gray-900">
                                                {video.title || video.original_filename}
                                              </p>
                                              <p className="text-xs text-gray-500">
                                                {video.format} • Google Drive • 
                                                Uploaded {new Date(video.created_at).toLocaleDateString()}
                                                {video.uploaded_by && ` by ${video.uploaded_by}`}
                                              </p>
                                              {video.platform_optimized && video.platform_optimized.length > 0 && (
                                                <div className="flex gap-1 mt-1">
                                                  {video.platform_optimized.map(platform => (
                                                    <span key={platform} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                                      {platform}
                                                    </span>
                                                  ))}
                                                </div>
                                              )}
                                            </div>
                                            <div className="flex items-center space-x-2 ml-4">
                                              <a
                                                href={video.public_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 flex items-center"
                                              >
                                                <Play className="w-3 h-3 mr-1" />
                                                View
                                              </a>
                                              <a
                                                href={video.public_url}
                                                download={video.original_filename}
                                                className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 flex items-center"
                                              >
                                                <Download className="w-3 h-3 mr-1" />
                                                Download
                                              </a>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Upload Completed Video</h3>
              <button
                onClick={() => setShowUploadModal(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <VideoUploadForm
              scriptId={showUploadModal}
              onUpload={handleVideoUpload}
              uploading={uploadingVideo.has(showUploadModal)}
              onCancel={() => setShowUploadModal(null)}
            />
          </div>
        </div>
      )}

      {/* Avatar Selection Modal */}
      {showAvatarModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Select Avatar</h3>
              <button
                onClick={() => {
                  setShowAvatarModal(false);
                  setAvatarModalScriptId(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {avatars.map((avatar) => {
                  const isSelected = selectedAvatars[avatarModalScriptId] === avatar.id;
                  
                  return (
                    <button
                      key={avatar.id}
                      onClick={() => {
                        setSelectedAvatars(prev => ({
                          ...prev,
                          [avatarModalScriptId]: avatar.id
                        }));
                        setShowAvatarModal(false);
                        setAvatarModalScriptId(null);
                      }}
                      className={`relative border-2 rounded-lg p-3 hover:shadow-lg transition-all ${
                        isSelected 
                          ? 'border-purple-500 bg-purple-50' 
                          : 'border-gray-200 hover:border-purple-300'
                      }`}
                    >
                      <img
                        src={avatar.thumbnail_url}
                        alt={`Avatar ${avatar.id}`}
                        className="w-full h-48 object-cover rounded"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="hidden items-center justify-center h-48 bg-gray-100 rounded">
                        <FileText className="w-12 h-12 text-gray-400" />
                      </div>
                      <div className="mt-2 text-xs text-gray-600 font-mono truncate">
                        {avatar.id.substring(0, 12)}...
                      </div>
                      {isSelected && (
                        <div className="absolute top-1 right-1 bg-purple-600 text-white rounded-full p-1">
                          <CheckCircle className="w-4 h-4" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              
              {avatars.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  No avatars available
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Video Upload Form Component
const VideoUploadForm = ({ scriptId, onUpload, uploading, onCancel }) => {
  const [googleDriveUrl, setGoogleDriveUrl] = useState('');
  const [title, setTitle] = useState('');
  const [format, setFormat] = useState('9:16');
  const [resolution, setResolution] = useState('');
  const [uploadNotes, setUploadNotes] = useState('');
  const [uploadedBy, setUploadedBy] = useState('');
  const [platforms, setPlatforms] = useState([]);

  // Remove file handling since we're using Google Drive links

  const handlePlatformChange = (platform) => {
    setPlatforms(prev => 
      prev.includes(platform) 
        ? prev.filter(p => p !== platform)
        : [...prev, platform]
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!googleDriveUrl) {
      alert('Please enter a Google Drive link');
      return;
    }

    // Basic validation for Google Drive URL
    if (!googleDriveUrl.includes('drive.google.com')) {
      alert('Please enter a valid Google Drive link');
      return;
    }

    const metadata = {
      google_drive_url: googleDriveUrl,
      title: title || 'Completed Video',
      format,
      resolution,
      upload_notes: uploadNotes,
      uploaded_by: uploadedBy,
      platform_optimized: platforms
    };

    onUpload(scriptId, null, metadata); // Pass null for file since we're using Google Drive
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Google Drive Video Link *
        </label>
        <input
          type="url"
          value={googleDriveUrl}
          onChange={(e) => setGoogleDriveUrl(e.target.value)}
          placeholder="https://drive.google.com/file/d/your-file-id/view"
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          required
        />
        <p className="text-xs text-gray-500 mt-1">
          Upload your video to Google Drive and paste the shareable link here
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Title (optional)
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Video title"
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Format
        </label>
        <select
          value={format}
          onChange={(e) => setFormat(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
        >
          <option value="9:16">Portrait (9:16)</option>
          <option value="16:9">Landscape (16:9)</option>
          <option value="1:1">Square (1:1)</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Resolution (optional)
        </label>
        <input
          type="text"
          value={resolution}
          onChange={(e) => setResolution(e.target.value)}
          placeholder="e.g., 1080x1920"
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Platform Optimized For
        </label>
        <div className="flex flex-wrap gap-2">
          {['YouTube', 'TikTok', 'Instagram', 'Facebook'].map(platform => (
            <label key={platform} className="flex items-center">
              <input
                type="checkbox"
                checked={platforms.includes(platform.toLowerCase())}
                onChange={() => handlePlatformChange(platform.toLowerCase())}
                className="mr-1"
              />
              <span className="text-sm">{platform}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Upload Notes (optional)
        </label>
        <textarea
          value={uploadNotes}
          onChange={(e) => setUploadNotes(e.target.value)}
          placeholder="Any notes about this video..."
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Uploaded By (optional)
        </label>
        <input
          type="text"
          value={uploadedBy}
          onChange={(e) => setUploadedBy(e.target.value)}
          placeholder="Video editor name"
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
        />
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={uploading || !googleDriveUrl}
          className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-md hover:bg-orange-700 disabled:opacity-50 flex items-center"
        >
          {uploading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              Upload Video
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default VideosPage;
